import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { getMe, changePassword, changeUsername, logout } from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { ArrowLeft, Lock, Mail, LogOut, Shield, Loader2, Eye, EyeOff, CheckCircle2, Link2 } from 'lucide-react';

export default function AdminSettings() {
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    (async () => {
      try { const m = await getMe(); setUsername(m.username); }
      catch { navigate('/admin/login'); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const doLogout = () => { logout(); navigate('/admin/login'); };

  if (loading) return <div className="min-h-screen grid place-items-center bg-[#faf7f2]"><Loader2 className="animate-spin text-neutral-400" /></div>;

  return (
    <div className="min-h-screen bg-[#faf7f2]">
      <nav className="bg-white border-b border-neutral-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/admin" className="inline-flex items-center gap-2 font-display font-bold text-neutral-900">
            <span className="w-8 h-8 rounded-lg bg-neutral-900 text-white grid place-items-center"><Link2 size={16} /></span>
            Biolink•br <span className="text-xs font-normal text-neutral-500 ml-1">configurações</span>
          </Link>
          <Button variant="outline" onClick={doLogout} className="gap-2 border-neutral-300"><LogOut size={14} /> Sair</Button>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        <div>
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-neutral-600 hover:text-neutral-900">
            <ArrowLeft size={14} /> Voltar ao Dashboard
          </Link>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <Shield className="text-neutral-900" size={22} />
            <h1 className="text-2xl font-display font-bold tracking-tight">Configurações de segurança</h1>
          </div>
          <p className="text-neutral-600 mt-1 text-sm">Gerencie as credenciais da conta de administrador. As senhas são armazenadas com hash seguro (pbkdf2-sha256).</p>
        </div>

        <section className="bg-white rounded-2xl border border-neutral-200 p-6">
          <h2 className="font-display font-bold text-lg mb-1">Conta do administrador</h2>
          <p className="text-xs text-neutral-500 mb-5">Login atual: <code className="bg-neutral-100 rounded px-1.5 py-0.5">{username}</code></p>
          <ChangeUsernameForm current={username} onDone={(u) => { setUsername(u); toast({ title: 'Login atualizado!', description: 'Faça login novamente com o novo usuário.' }); doLogout(); }} />
        </section>

        <section className="bg-white rounded-2xl border border-neutral-200 p-6">
          <h2 className="font-display font-bold text-lg mb-5">Alterar senha</h2>
          <ChangePasswordForm onDone={() => { toast({ title: 'Senha alterada!', description: 'Faça login novamente com a nova senha.' }); doLogout(); }} />
        </section>

        <section className="bg-neutral-50 rounded-2xl border border-neutral-200 p-5 text-sm text-neutral-600">
          <div className="flex gap-2"><CheckCircle2 size={16} className="text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <b>Boas práticas aplicadas:</b> credenciais armazenadas somente no backend com hash pbkdf2-sha256, bloqueio temporário após 10 tentativas malsucedidas em 15 minutos, token Bearer em cada chamada administrativa e nenhum segredo exposto no código do frontend.
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

function ChangeUsernameForm({ current, onDone }) {
  const [newUsername, setNewUsername] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (!newUsername.trim() || newUsername.trim() === current) { setErr('Informe um login diferente do atual.'); return; }
    setBusy(true);
    try { const r = await changeUsername(currentPassword, newUsername.trim()); onDone(r.username); }
    catch (e) { setErr(e.response?.data?.detail || 'Erro ao alterar login.'); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 gap-4">
      <div className="sm:col-span-2">
        <Label>Novo login (ou e-mail)</Label>
        <div className="relative mt-1">
          <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={newUsername} onChange={(e) => setNewUsername(e.target.value)} placeholder="ex: admin@suaempresa.com" className="pl-9" />
        </div>
      </div>
      <div className="sm:col-span-2">
        <Label>Senha atual</Label>
        <div className="relative mt-1">
          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input type={showPwd ? 'text' : 'password'} value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="pl-9 pr-10" required />
          <button type="button" onClick={() => setShowPwd(!showPwd)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-900">
            {showPwd ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
      {err && <div className="sm:col-span-2 text-sm text-red-600">{err}</div>}
      <div className="sm:col-span-2 flex justify-end">
        <Button type="submit" disabled={busy} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white">
          {busy && <Loader2 className="animate-spin" size={14} />} Salvar novo login
        </Button>
      </div>
    </form>
  );
}

function ChangePasswordForm({ onDone }) {
  const [current, setCurrent] = useState('');
  const [p1, setP1] = useState('');
  const [p2, setP2] = useState('');
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const strength = p1.length >= 12 ? 'forte' : p1.length >= 8 ? 'boa' : p1.length > 0 ? 'fraca' : '';
  const colorMap = { fraca: 'bg-red-500', boa: 'bg-amber-500', forte: 'bg-emerald-500' };

  const submit = async (e) => {
    e.preventDefault(); setErr('');
    if (p1.length < 8) { setErr('A nova senha precisa ter pelo menos 8 caracteres.'); return; }
    if (p1 !== p2) { setErr('A confirmação não confere com a nova senha.'); return; }
    setBusy(true);
    try { await changePassword(current, p1); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || 'Erro ao alterar a senha.'); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <Label>Senha atual</Label>
        <div className="relative mt-1">
          <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input type={show ? 'text' : 'password'} value={current} onChange={(e) => setCurrent(e.target.value)} className="pl-9 pr-10" required />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-900">
            {show ? <EyeOff size={14} /> : <Eye size={14} />}
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <Label>Nova senha</Label>
          <div className="relative mt-1">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input type={show ? 'text' : 'password'} value={p1} onChange={(e) => setP1(e.target.value)} minLength={8} className="pl-9" required />
          </div>
          {strength && (
            <div className="mt-1.5">
              <div className="h-1 rounded-full bg-neutral-200 overflow-hidden"><div className={`h-full ${colorMap[strength]}`} style={{ width: strength === 'fraca' ? '33%' : strength === 'boa' ? '66%' : '100%' }} /></div>
              <div className="text-xs text-neutral-500 mt-1">Força: {strength}</div>
            </div>
          )}
        </div>
        <div>
          <Label>Confirmar nova senha</Label>
          <div className="relative mt-1">
            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <Input type={show ? 'text' : 'password'} value={p2} onChange={(e) => setP2(e.target.value)} minLength={8} className="pl-9" required />
          </div>
        </div>
      </div>
      {err && <div className="text-sm text-red-600">{err}</div>}
      <div className="flex justify-end">
        <Button type="submit" disabled={busy} className="gap-2 bg-neutral-900 hover:bg-neutral-800 text-white">
          {busy && <Loader2 className="animate-spin" size={14} />} Alterar senha
        </Button>
      </div>
    </form>
  );
}
